import { ENTITLEMENT_NOTE_MAX_LENGTH } from '@aysar/contracts';

import {
  type CustomFieldControl,
  type FieldDef,
} from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { ServerSelect } from '@/shared/components/selection/ServerSelect/ServerSelect';
import {
  usePlanOptionsQuery,
  useResolveProductKeys,
} from '@/shared/services/subscription-product-lookup/subscription-product-lookup.api';

import { PromoDurationField } from '../components/PromoDurationField/PromoDurationField';

/** The module's translate function, narrowed the way `Translate` is elsewhere. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface PromoGrantFieldsOptions {
  /**
   * The address the operator must type back, or `null` when this grant adds
   * rather than replaces — or when nobody can read the member's address.
   */
  confirmPhrase: string | null;
}

/**
 * Comping an account.
 *
 * ## There is no member picker
 *
 * The member is whoever the screen is showing, handed in and displayed as a
 * read-only fact. Two reasons, and the second is the load-bearing one: a grant
 * made from a viewer is always *for the account in front of you*, and a picker
 * would need `members.read` — a permission `billing.manage` does not imply — so
 * an operator able to comp an account would be unable to name one.
 *
 * ## The duration is three cards, not a number box
 *
 * See `PromoDurationField`: an absent key, a `null` and a number are three
 * different instructions to the api, and only one of them is expressible as a
 * number.
 *
 * ## The phrase appears only for the write that DESTROYS something
 *
 * `grantOrExtend` overwrites a live comp's plan, tier and period. When that is
 * what the submit is about to do, the operator types the member's address back —
 * read off the row, never the word DELETE — and it is dropped entirely when the
 * address cannot be resolved, because a dialog nobody can confirm is worse than
 * none (P22b4). It is a control, never part of the request: `PromoGrantCreateSchema`
 * is `.strict()`.
 */
export function buildPromoGrantFields(
  t: Translate,
  { confirmPhrase }: PromoGrantFieldsOptions,
): FieldDef[] {
  const fields: FieldDef[] = [
    {
      name: 'productKey',
      kind: 'custom',
      label: t('entitlements.grant.plan'),
      description: t('entitlements.grant.planHint'),
      isRequired: true,
      colSpan: 'full',
      render: (control: CustomFieldControl) => (
        <ServerSelect
          id={control.id}
          value={readText(control.value)}
          isDisabled={control.isDisabled}
          isInvalid={control.isInvalid}
          isClearable
          placeholder={t('entitlements.grant.planPlaceholder')}
          aria-describedby={control.describedById}
          // Plans only (D9b2): a fee grants no entitlement, and the api refuses
          // one at the door — so the picker never offers it.
          useOptionsQuery={usePlanOptionsQuery}
          useResolveIds={useResolveProductKeys}
          onBlur={control.onBlur}
          onValueChange={(value) => {
            // `''` rather than `null`: the field is a required key, and an empty
            // string is what an unanswered `ServerSelect` reports.
            control.onChange(value ?? '');
          }}
        />
      ),
    },
    {
      name: 'durationDays',
      kind: 'custom',
      label: t('entitlements.grant.duration.label'),
      description: t('entitlements.grant.duration.hint'),
      colSpan: 'full',
      // The control is a `role="group"` carrying its own `aria-label`, so the
      // form must NOT print a `<Label htmlFor>` pointing at an element that does
      // not carry the id.
      isSelfNaming: true,
      render: (control: CustomFieldControl) => (
        <PromoDurationField
          value={control.value}
          isDisabled={control.isDisabled}
          label={t('entitlements.grant.duration.label')}
          describedById={control.describedById}
          onBlur={control.onBlur}
          onChange={control.onChange}
        />
      ),
    },
    {
      name: 'note',
      kind: 'textarea',
      label: t('entitlements.grant.note'),
      description: t('entitlements.grant.noteHint'),
      isRequired: true,
      rows: 3,
      maxLength: ENTITLEMENT_NOTE_MAX_LENGTH,
      colSpan: 'full',
    },
  ];

  if (confirmPhrase !== null) {
    fields.push({
      name: 'confirmPhrase',
      kind: 'text',
      label: t('entitlements.grant.replace.typeEmail', { email: confirmPhrase }),
      description: t('entitlements.grant.replace.typeEmailHint'),
      isRequired: true,
      colSpan: 'full',
      // The schema refuses a mismatch with Zod's `custom` code, which carries no
      // sentence the kit can classify — so the translated one is supplied here
      // (P10.6e2).
      invalidMessage: t('entitlements.grant.replace.typeEmailInvalid', { email: confirmPhrase }),
    });
  }

  return fields;
}

/**
 * A `custom` control's value crosses the form as `unknown`, so it is narrowed
 * rather than asserted (U4). `''` reads as unanswered, which is what
 * `ServerSelect` wants as `null`.
 */
function readText(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}
